import { useEffect, useState } from 'react'
import { useParams } from 'wouter'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { AlertCircle, Lock } from 'lucide-react'
import CostSavingsSummary from '@/components/CostSavingsSummary'

interface PublicProject {
  id: string
  name: string
  organization?: string
  summary_data?: any
}

export default function PublicCostSavingsSummary() {
  const { token } = useParams()
  const [project, setProject] = useState<PublicProject | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPublicProject = async () => {
      try {
        setLoading(true)
        const response = await fetch(`/api/public/cost-savings-summary/${token}`)
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('This summary link is not valid or has expired.')
          } else {
            setError('Unable to load the cost savings summary.')
          }
          return
        }

        const data = await response.json()
        setProject(data)
      } catch (err) {
        console.error('Error fetching public project:', err)
        setError('Unable to load the cost savings summary.')
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      fetchPublicProject()
    }
  }, [token])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-muted-foreground">Loading cost savings summary...</p>
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-500/20 bg-red-500/5">
          <CardContent className="p-6 text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Access Denied</h3>
              <p className="text-muted-foreground text-sm">
                {error || 'This summary link is not valid or has expired.'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Public Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Lock className="w-5 h-5 text-blue-400" />
                <h1 className="text-xl font-semibold text-foreground">
                  Cost Savings Summary
                </h1>
              </div>
              {project.organization && (
                <Badge variant="outline" className="border-blue-500/60 text-blue-300 bg-blue-500/10">
                  {project.organization}
                </Badge>
              )}
            </div>
            <Badge variant="outline" className="border-emerald-500/60 text-emerald-300 bg-emerald-500/10">
              Public View
            </Badge>
          </div>
          
          <div className="mt-2">
            <p className="text-sm text-muted-foreground">
              Read-only summary for: <span className="font-medium text-foreground">{project.name}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Cost Savings Summary Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <CostSavingsSummary 
          projectId={project.id} 
          isPublicView={true}
          publicProjectData={project}
          showExportOptions={false}
          showFinancials={true}
        />
      </div>
    </div>
  )
}